import { type ComponentPropsWithoutRef, forwardRef, memo, type ReactNode } from 'react';

import { Command } from '@air/cmdk';
import { ImageSearchIcon } from '@air/design-icons';
import { cn } from '@air/utils-tailwind';

import { usePrivateSearchUIHelpers } from '~/components/Search/hooks/usePrivateSearchUIHelpers';
import { searchFieldIconSlot, searchFieldText } from '~/components/Search/searchFieldChrome';
import { type SearchCommandFilterCountProps } from '~/components/SearchCommand/SearchCommandFilterCount';
import { SearchCommandInputPlaceholder } from '~/components/SearchCommand/SearchCommandInputPlaceholder';
import { SearchCommandModeSelector } from '~/components/SearchCommand/SearchCommandModeSelector';
import { SearchCommandVisualSearchButton } from '~/components/SearchCommand/SearchCommandVisualSearchButton';
import { useAppliedFiltersCount } from '~/hooks/filters/useAppliedFiltersCount';

export type SmartSearchCommandInputProps = Pick<
  ComponentPropsWithoutRef<typeof Command.Input>,
  'className' | 'defaultValue' | 'onValueChange' | 'placeholder' | 'value'
> &
  Partial<Pick<SearchCommandFilterCountProps, 'count'>> & {
    children?: ReactNode;
    compact?: boolean;
    onVisualSearchClick: (() => void) | undefined;
    visualSearchTooltip: string;
  };

export const SmartSearchCommandInput = memo(
  forwardRef<HTMLInputElement, SmartSearchCommandInputProps>(
    (
      {
        children,
        className,
        compact,
        defaultValue,
        onValueChange,
        onVisualSearchClick,
        placeholder,
        value,
        visualSearchTooltip,
      }: SmartSearchCommandInputProps,
      ref,
    ) => {
      const { similarToImageSearchAvailable, isActiveImageSearch } = usePrivateSearchUIHelpers();
      const { filtersCanBeSaved } = useAppliedFiltersCount();
      const canShowVisualSearchAffordance =
        similarToImageSearchAvailable && !value && !isActiveImageSearch && !filtersCanBeSaved;
      const shouldShowVisualSearchButton = canShowVisualSearchAffordance && !!onVisualSearchClick;
      const shouldShowVisualSearchIcon = canShowVisualSearchAffordance && !onVisualSearchClick;

      return (
        <div
          className={cn(
            'relative flex h-full min-h-0 w-full shrink-0 items-stretch gap-1 overflow-hidden border border-transparent px-1 outline-none',
            className,
          )}
          data-testid="SEARCH_COMMAND_INPUT"
        >
          <div className="relative order-2 flex min-w-0 flex-1 items-center self-stretch py-1">
            <Command.Input
              className={cn(
                searchFieldText(),
                'h-auto min-h-0 w-full',
                compact ? 'bg-transparent pr-10 text-14 placeholder:text-transparent' : undefined,
                className,
              )}
              data-testid="SEARCH_COMMAND_INPUT_INPUT"
              defaultValue={defaultValue}
              onValueChange={onValueChange}
              placeholder={placeholder}
              ref={ref}
              value={value}
            />
            {compact && !value && placeholder && (
              <SearchCommandInputPlaceholder className="left-0" placeholder={placeholder} />
            )}
          </div>
          <span className="order-1 flex shrink-0 items-center self-center" data-testid="SEARCH_COMMAND_LEADING_SLOT">
            <SearchCommandModeSelector search={value} showTooltip={false} />
          </span>
          {shouldShowVisualSearchIcon && (
            <span className={cn('order-3 ml-auto', searchFieldIconSlot())} data-testid="SEARCH_COMMAND_TRAILING_SLOT">
              <ImageSearchIcon
                className="shrink-0 ads:text-icon-default text-gray-10"
                data-testid="SEARCH_COMMAND_IMAGE_SEARCH_ICON"
                size="sm"
              />
            </span>
          )}
          {children ? (
            <span className="order-last flex shrink-0 items-center gap-2 ui2026:gap-1">{children}</span>
          ) : null}
          {shouldShowVisualSearchButton && onVisualSearchClick && (
            <span className="order-last ml-auto flex shrink-0 items-center" data-testid="SEARCH_COMMAND_TRAILING_SLOT">
              <SearchCommandVisualSearchButton onClick={onVisualSearchClick} tooltip={visualSearchTooltip} />
            </span>
          )}
        </div>
      );
    },
  ),
);

SmartSearchCommandInput.displayName = 'SmartSearchCommandInput';
